/**
 * Viewer Rail — the deliverable switcher for the project viewer, as an icon rail
 * on the left (mirrors the admin shell's navigation rail). Enabled deliverables
 * come from the project; callers can append extra entries such as the admin
 * Data Management page. Hidden on phones, where the floating menu takes over.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../store/AppContext';
import { TABS, getEnabledTabs } from '../../../shared/types/project';
import type { TabId } from '../../../shared/types/project';
import { TabIcons } from './TabBar';

export interface ViewerRailLink {
  key: string;
  label: string;
  to: string;
  icon: ReactNode;
}

export function ViewerRail({ links = [] }: { links?: ViewerRailLink[] }) {
  const { state, setActiveTab, dispatch } = useApp();
  const enabled = getEnabledTabs(state.currentProject);
  const tabs = TABS.filter((t) => enabled.includes(t.id));

  const select = (id: TabId) => {
    if (state.dualViewMode) dispatch({ type: 'TOGGLE_DUAL_VIEW' });
    setActiveTab(id);
  };

  return (
    <nav className="viewer-rail" aria-label="Deliverables">
      {tabs.map((tab) => {
        const active = state.activeTab === tab.id && !state.dualViewMode;
        return (
          <button
            key={tab.id}
            type="button"
            className={`viewer-rail__item ${active ? 'viewer-rail__item--active' : ''}`}
            onClick={() => select(tab.id)}
            title={`${tab.label} (${tab.shortcut})`}
            aria-pressed={active}
          >
            {TabIcons[tab.icon]}
          </button>
        );
      })}
      {links.length > 0 && <span className="viewer-rail__divider" />}
      {links.map((link) => (
        <Link key={link.key} to={link.to} className="viewer-rail__item" title={link.label}>
          {link.icon}
        </Link>
      ))}
    </nav>
  );
}
